/**
 * Beyond Sure — original AI-agent avatar mark.
 *
 * IP guardrail: this is BeyondSure's OWN mark — a stylised wave/"S"
 * inside a circular green-glass badge. It is intentionally NOT a copy
 * of Anthropic's asterisk, OpenAI's flower, Grok's slash, or any other
 * AI assistant's mark.
 *
 * Sizes follow Claude's avatar scale: 24px inline (message-row), 32px
 * leading the first turn, 56px in the empty state. Pass `size` to pick.
 */

interface AgentAvatarProps {
  size?: number;
  /** Show a soft "live" pulse around the badge for streaming state. */
  pulse?: boolean;
  /** Optional aria-label override. */
  ariaLabel?: string;
}

export function AgentAvatar({
  size = 32,
  pulse = false,
  ariaLabel = "Beyond Sure advisor",
}: AgentAvatarProps) {
  const px = `${size}px`;
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      data-testid="agent-avatar"
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: px, height: px }}
    >
      {pulse ? (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bsure-pulse"
          style={{
            background:
              "radial-gradient(circle, rgba(62,142,91,0.45) 0%, rgba(62,142,91,0) 70%)",
          }}
        />
      ) : null}
      <span
        aria-hidden
        className="relative inline-flex items-center justify-center rounded-full"
        style={{
          width: px,
          height: px,
          // Brand-glass badge: deep green core, soft inner ring, frosted hairline.
          background:
            "radial-gradient(circle at 30% 28%, var(--color-brand) 0%, var(--color-brand-deep) 75%)",
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.08), 0 8px 18px -8px rgba(20,75,51,0.45)",
        }}
      >
        {/*
          The mark itself — a stylised wave that doubles as an "S".
          Drawn at 24×24 viewbox so it scales cleanly from 20px to 96px.
        */}
        <svg
          viewBox="0 0 24 24"
          width={Math.round(size * 0.62)}
          height={Math.round(size * 0.62)}
          fill="none"
          aria-hidden
        >
          <path
            d="M17.5 7.5C16.4 6.2 14.7 5.5 12.8 5.5C9.8 5.5 7.5 7.4 7.5 9.7C7.5 11.6 9.0 12.7 11.3 13.4L13.4 14.0C15.0 14.5 15.8 14.9 15.8 15.8C15.8 16.7 14.8 17.4 13.0 17.4C10.9 17.4 9.5 16.6 8.5 15.5"
            stroke="rgba(255,255,255,0.96)"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Subtle spark accent above the wave to lift it from a literal S. */}
          <circle cx="18.2" cy="6.0" r="1.0" fill="rgba(255,255,255,0.85)" />
        </svg>
      </span>
    </span>
  );
}

export default AgentAvatar;
